import { ReactNode, useMemo } from 'react';
import defineAbilityFor from 'src/config/ability';
import { useAuth } from 'src/features/authentication';
import { useGetLoggedInUserQuery } from 'src/services/restApi/queries/users.service';
import { AbilityContext } from './canContext';

/**
 * Builds the CASL ability instance from the currently logged-in user's role
 * and provides it via AbilityContext. Must live inside <AuthProvider> so the
 * Firebase auth hook is available.
 *
 * Anonymous users get an empty-but-non-null ability so consumers don't crash;
 * the `useGetLoggedInUserQuery` skip-token short-circuits the network call
 * until auth is settled.
 */
export const AbilityProvider = ({ children }: { children: ReactNode }) => {
  const { user: authUser } = useAuth();
  const { data: currentUser } = useGetLoggedInUserQuery(authUser?.uid ?? '', {
    skip: !authUser?.uid,
  });

  // Re-build the ability instance only when the role string changes. Stable
  // identity keeps the provider from re-rendering descendants on every tick.
  const ability = useMemo(
    () => defineAbilityFor(currentUser?.role ?? null),
    [currentUser?.role],
  );

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
};
